import { inject, Injectable } from "@angular/core";
import {
    HttpInterceptor,
    HttpEvent,
    HttpHandler,
    HttpRequest,
    HttpErrorResponse,
} from "@angular/common/http";
import { catchError, Observable, throwError } from "rxjs";
import { TranslocoService } from "@jsverse/transloco";

import { NewResponseBody } from "src/app/shared/model/new-response-body.model";
import { translateServerError } from "../utils/functions/http-error-translator.function";

export class ServerError {
    code: string;
    message: string;
    status: number;
    isValid: false;
    params: Record<string, string | number>;

    constructor(data: Partial<ServerError>) {
        Object.assign(this, data);
    }
}

/**
 * Interceptor that translates server errors into human-readable messages.
 * @see {@link https://atomchat.atlassian.net/wiki/x/IAAHoQ}
 */
@Injectable({
    providedIn: "root",
})
export class HttpErrorTranslatorInterceptor implements HttpInterceptor {
    private readonly translocoService = inject(TranslocoService);

    intercept(
        req: HttpRequest<unknown>,
        next: HttpHandler,
    ): Observable<HttpEvent<unknown>> {
        return next.handle(req).pipe(
            catchError((error) => {
                if (error instanceof HttpErrorResponse) {
                    const errorResponse =
                        error.error as NewResponseBody<ServerError>;
                    if (errorResponse?.data && "code" in errorResponse.data) {
                        const newError = this.generateHttpErrorResponse(
                            error,
                            errorResponse,
                        );
                        return throwError(() => newError);
                    }
                }
                return throwError(() => error as Error);
            }),
        );
    }

    /**
     * Generates an HttpErrorResponse object with the translated error message.
     * @param errorResponse The error object.
     */
    private generateHttpErrorResponse(
        httpErrorResponse: HttpErrorResponse,
        errorResponse: NewResponseBody<ServerError>,
    ): HttpErrorResponse {
        const { data } = errorResponse;
        data.message = translateServerError(data, this.translocoService);
        const serverError = new ServerError(data);
        const responseBody = new NewResponseBody<ServerError>({
            ...errorResponse,
            data: serverError,
        });
        return new HttpErrorResponse({
            ...httpErrorResponse,
            error: responseBody,
        });
    }
}
